import { Box, ButtonBase, Typography, alpha } from "@mui/material";

export interface ColorVehiculo {
  id: string;
  /** Nombre comercial del color, ej. "Apricity White". */
  nombre: string;
  /** Color de la muestra. */
  hex: string;
  /** Url completa de la foto de la unidad en ese color. */
  img: string;
}

interface Props {
  /** Marca y modelo, ej. "BYD Dolphin Mini Plus". */
  nombre: string;
  /** Línea bajo el nombre, ej. "2026 · Eléctrico · 380 km de autonomía". */
  meta: string;
  colores: ColorVehiculo[];
  /** `id` del color elegido. */
  value: string;
  onChange: (id: string) => void;
  testId: string;
}

/**
 * Replica `.veh-hero` del cotizador de WuWei: foto de la unidad (las de todos los colores encimadas, solo se ve la
 * elegida) y, a la derecha, nombre, datos y las muestras de color. La muestra elegida va en `primary.accent`.
 */
export const CardVehiculoCotizador = ({ nombre, meta, colores, value, onChange, testId }: Props) => {
  const elegido = colores.find((color) => color.id === value) ?? colores[0];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.15fr 1fr" },
        alignItems: "center",
        gap: 1,
        overflow: "hidden",
        bgcolor: "background.paper",
        border: "1.5px solid",
        borderColor: "divider",
        borderRadius: "14px",
        mb: 2,
      }}
    >
      <Box
        sx={(theme) => ({
          alignSelf: "stretch",
          display: "grid",
          alignItems: "center",
          p: "18px 6px 18px 22px",
          background: `linear-gradient(135deg, ${theme.palette.tokens?.surface2}, color-mix(in oklab, ${theme.palette.primary.accent} 6%, ${theme.palette.tokens?.surface2}))`,
        })}
      >
        {colores.map((color) => (
          <Box
            key={color.id}
            component="img"
            src={color.img}
            alt={`${nombre} en ${color.nombre}`}
            sx={{
              gridArea: "1 / 1",
              width: "100%",
              height: "auto",
              maxHeight: 230,
              objectFit: "contain",
              opacity: color.id === elegido?.id ? 1 : 0,
              transition: "opacity .15s ease",
            }}
          />
        ))}
      </Box>

      <Box sx={{ p: "22px 24px" }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em", color: "text.primary" }}>
          {nombre}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "tokens.textTertiary", mt: "3px" }}>{meta}</Typography>
        <Typography sx={{ fontSize: 12, color: "tokens.textTertiary", mt: 2 }}>
          Color ·{" "}
          <Box component="strong" sx={{ color: "text.primary", fontWeight: 600 }}>
            {elegido?.nombre}
          </Box>
        </Typography>
        <Box sx={{ display: "flex", gap: "10px", mt: 1 }}>
          {colores.map((color) => {
            const on = color.id === elegido?.id;
            return (
              <ButtonBase
                key={color.id}
                title={color.nombre}
                aria-label={color.nombre}
                aria-pressed={on}
                onClick={() => onChange(color.id)}
                data-testid={`${testId}-${color.id}`}
                sx={(theme) => ({
                  width: 34,
                  height: 34,
                  p: "3px",
                  borderRadius: "50%",
                  bgcolor: "background.paper",
                  border: "1.5px solid",
                  borderColor: on ? theme.palette.primary.accent : theme.palette.divider,
                  boxShadow: on ? `0 0 0 3px ${alpha(theme.palette.primary.accent, 0.15)}` : "none",
                  transition: "border-color .14s, box-shadow .14s",
                  "&:hover": { borderColor: on ? undefined : alpha(theme.palette.primary.accent, 0.45) },
                })}
              >
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    bgcolor: color.hex,
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                  }}
                />
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
